/* eslint-disable max-len */
import React from 'react';
import { FormattedMessage } from 'react-intl';
import countries from 'i18n-iso-countries';
import { buildCountryOptions, EUCountries } from '../../utilities/regions';
import {
  isValidEmail, isVATValid, isVATRequired, VATregex, isValidZip,
} from '../form/validation';
import ApiClient from '../../loopback/apiClient';

export const optionDisplayValues = {
  male: <FormattedMessage id="salutation.mr" />,
  female: <FormattedMessage id="salutation.ms" />,
  employee: <FormattedMessage id="registration.userPositionEmployee" />,
  director: <FormattedMessage id="registration.userPositionDirector" />,
};

const requiredField = (value) => (value && value.toString().trim() ? '' : <FormattedMessage id="registration.required" />);
const validEmail = (email) => (isValidEmail(email) ? '' : <FormattedMessage id="registration.noValidMail" />);
const validZip = (zip) => (isValidZip(zip) ? '' : <FormattedMessage id="registration.noValidZip" />);
const emailDoesNotExist = async (email) => {
  const result = await ApiClient.emailExists(email);
  if (result) {
    return <FormattedMessage id="registration.emailAlreadyExists" />;
  }
  return '';
};
// this was initially done at record-level
// however due to a bug with aynch validation for email,
// im changing this to use record level.
const mustMatchWith = (value, matchingValue) => (value !== matchingValue ? <FormattedMessage id="registration.mustMatch" /> : '');
const composeValidators = (...validators) => (value, fields) => validators.reduce((error, validator) => error || validator(value, fields), undefined);

const hideCustomsInfo = [...EUCountries, 'CH'];

const instituteInvoiceAddressConditionalShow = {
  when: 'institute.invoicedifferent',
  is: false,
  reset: 'institute.invoice',
};

const instituteCertificateAddressConditionalShow = {
  when: 'institute.certificatedifferent',
  is: false,
  reset: 'institute.certificate',
};

const registerFormConfig = (locale, isAdmin) => [
  {
    label: <FormattedMessage id="registration.personalInformationHeading" />,
    formRef: 'personal_form',
    fields: [
      {
        label: <FormattedMessage id="registration.userGender" />,
        key: 'userGender',
        type: 'select',
        options: [
          {
            viewValue: optionDisplayValues.male,
            value: 'male',
          },
          {
            viewValue: optionDisplayValues.female,
            value: 'female',
          },
        ],
        columnClassName: 'basis-50',
        validate: requiredField,
      },
      {
        label: <FormattedMessage id="registration.userTitle" />,
        key: 'userTitle',
        type: 'text',
        columnClassName: 'basis-50',
      },
      {
        label: <FormattedMessage id="registration.userFirstName" />,
        key: 'userFirstName',
        columnClassName: 'basis-50',
        type: 'text',
        validate: requiredField,
      },
      {
        label: <FormattedMessage id="registration.userLastName" />,
        key: 'userLastName',
        columnClassName: 'basis-50',
        type: 'text',
        validate: requiredField,
      },
      {
        label: <FormattedMessage id="registration.userEmail" />,
        key: 'userEmail',
        type: 'email',
        columnClassName: 'basis-100',
        validate: composeValidators(requiredField, validEmail, emailDoesNotExist),
      },
      {
        label: <FormattedMessage id="registration.userPassword" />,
        key: 'userPassword',
        type: 'password',
        columnClassName: 'basis-50',
        validate: requiredField,
      },
      {
        label: <FormattedMessage id="registration.userPasswordConfirm" />,
        key: 'userPasswordConfirm',
        type: 'password',
        ref: 'passwordConfirm',
        columnClassName: 'basis-50',
        required: true,
        validate: composeValidators(requiredField, (value, { userPassword }) => mustMatchWith(value, userPassword)),
      },
      {
        label: <FormattedMessage id="registration.userCountry" />,
        key: 'userCountry',
        type: 'select',
        required: false,
        columnClassName: 'basis-100',
        options: buildCountryOptions(locale, isAdmin),
        validate: requiredField,
      },
    ],
    requiredMessage: <FormattedMessage id="registration.requiredMessage" />,
  },
  {
    label: <FormattedMessage id="registration.instituteInformationHeading" />,
    about: <FormattedMessage id="registration.aboutDeliveryAddress" />,
    formRef: 'institute_form',
    fields: [
      {
        label: <FormattedMessage id="registration.hospitalName" />,
        key: 'institute.hospitalName',
        type: 'text',
        columnClassName: 'basis-50',
      },
      {
        label: <FormattedMessage id="registration.instituteName" />,
        key: 'institute.instituteName',
        columnClassName: 'basis-50',
        type: 'text',
        validate: requiredField,
      },
      {
        label: <FormattedMessage id="registration.instituteStreet" />,
        key: 'institute.instituteStreet',
        columnClassName: 'basis-50',
        type: 'text',
        validate: requiredField,
      },
      {
        label: <FormattedMessage id="registration.instituteHousenumber" />,
        key: 'institute.instituteHousenumber',
        columnClassName: 'basis-50',
        type: 'text',
        validate: requiredField,
      },
      {
        label: <FormattedMessage id="registration.instituteAdditionalAddress" />,
        key: 'institute.instituteAdditionalAddress',
        columnClassName: 'basis-100',
        type: 'text',
      },
      {
        label: <FormattedMessage id="registration.instituteCity" />,
        key: 'institute.instituteCity',
        columnClassName: 'basis-50',
        type: 'text',
        validate: requiredField,
      },
      {
        label: <FormattedMessage id="registration.instituteZip" />,
        key: 'institute.instituteZip',
        columnClassName: 'basis-50',
        type: 'text',
        validate: composeValidators(requiredField, validZip),
      },
      {
        label: <FormattedMessage id="registration.instituteCountry" />,
        key: 'institute.instituteCountry',
        type: 'select',
        required: false,
        columnClassName: 'basis-50',
        options: buildCountryOptions(locale, isAdmin),
        validate: requiredField,
      },
      {
        label: <FormattedMessage id="registration.instituteTaxID" />,
        key: 'institute.instituteTaxID',
        type: 'text',
        columnClassName: 'basis-50',
        // eslint-disable-next-line react/display-name
        validate: composeValidators(requiredField, (value, { institute }) => {
          if (!institute) {
            return '';
          }

          const { instituteCountry = undefined } = institute;

          const valid = isVATValid(value, instituteCountry);
          if (valid && valid.length) {
            return '';
          }
          const errorOptions = {
            country: countries.getName(instituteCountry, locale),
            example: '[0-9][A-Z][a-z][+-./()]',
          };
          if (instituteCountry && !!VATregex[instituteCountry]) {
            errorOptions.example = VATregex[instituteCountry].example;
          }
          return <FormattedMessage id="registration.noValidVat" values={errorOptions} />;
        }),
        conditional: {
          when: 'institute.instituteCountry',
          is: 'DE',
          reset: 'institute.custom',
        },
      },
      {
        label: <FormattedMessage id="registration.institutePhone" />,
        key: 'institute.institutePhone',
        columnClassName: 'basis-50',
        type: 'text',
        required: false,
        validate: requiredField,
      },
      {
        label: <FormattedMessage id="registration.instituteFax" />,
        key: 'institute.instituteFax',
        type: 'text',
        columnClassName: 'basis-50',
      },
      {
        label: <FormattedMessage id="registration.instituteEmail" />,
        type: 'email',
        key: 'institute.instituteEmail',
        columnClassName: 'basis-100',
        validate: composeValidators(requiredField, validEmail),
      },
      {
        label: <FormattedMessage id="registration.instituteComments" />,
        type: 'text',
        key: 'institute.instituteComments',
        columnClassName: 'basis-100',
      },
      {
        label: <FormattedMessage id="registration.patientCare" />,
        key: 'institute.isCompany',
        type: 'checkbox',
        defaultValue: false,
        columnClassName: 'basis-100',
      },
      {
        label: <FormattedMessage id="registration.clearanceHandlerHeading" />,
        type: 'info',
        key: 'institute.custom.info',
        conditional: {
          when: 'institute.instituteCountry',
          is: hideCustomsInfo,
          reset: 'institute.custom',
        },
      },
      {
        label: <FormattedMessage id="registration.customClearanceUser" />,
        key: 'institute.custom.customClearanceUser',
        columnClassName: 'basis-100',
        validate: requiredField,
        type: 'text',
        conditional: {
          when: 'institute.instituteCountry',
          is: hideCustomsInfo,
        },
      },
      {
        label: <FormattedMessage id="registration.customClearanceContact" />,
        key: 'institute.custom.customClearanceContact',
        columnClassName: 'basis-50',
        validate: requiredField,
        type: 'text',
        conditional: {
          when: 'institute.instituteCountry',
          is: hideCustomsInfo,
        },
      },
      {
        label: <FormattedMessage id="registration.customClearanceEmail" />,
        key: 'institute.custom.customClearanceEmail',
        columnClassName: 'basis-50',
        type: 'email',
        validate: composeValidators(requiredField, validEmail),
        conditional: {
          when: 'institute.instituteCountry',
          is: hideCustomsInfo,
        },
      },
      {
        label: <FormattedMessage id="registration.eoriNumber" />,
        key: 'institute.custom.eoriNumber',
        columnClassName: 'basis-100',
        type: 'text',
        conditional: {
          when: 'institute.instituteCountry',
          is: hideCustomsInfo,
        },
      },
      {
        label: <FormattedMessage id="registration.invoicedifferent" />,
        key: 'institute.invoicedifferent',
        type: 'checkbox',
        defaultValue: false,
        columnClassName: 'basis-100',
      },
      {
        label: <FormattedMessage id="registration.invoiceInformationHeading" />,
        type: 'info',
        key: 'institute.invoice.info',
        conditional: instituteInvoiceAddressConditionalShow,
      },
      {
        label: <FormattedMessage id="registration.invoiceName" />,
        key: 'institute.invoice.invoiceName',
        columnClassName: 'basis-50',
        type: 'text',
        conditional: instituteInvoiceAddressConditionalShow,
        validate: requiredField,
      },
      {
        label: <FormattedMessage id="registration.invoiceStreet" />,
        type: 'text',
        key: 'institute.invoice.invoiceStreet',
        columnClassName: 'basis-50',
        conditional: instituteInvoiceAddressConditionalShow,
        validate: requiredField,
      },
      {
        label: <FormattedMessage id="registration.invoiceHousenumber" />,
        type: 'text',
        key: 'institute.invoice.invoiceHousenumber',
        columnClassName: 'basis-50',
        conditional: instituteInvoiceAddressConditionalShow,
        validate: requiredField,
      },
      {
        label: <FormattedMessage id="registration.invoiceZip" />,
        type: 'text',
        key: 'institute.invoice.invoiceZip',
        columnClassName: 'basis-50',
        validate: requiredField,
        conditional: instituteInvoiceAddressConditionalShow,
      },
      {
        label: <FormattedMessage id="registration.invoiceCity" />,
        type: 'text',
        key: 'institute.invoice.invoiceCity',
        columnClassName: 'basis-50',
        validate: requiredField,
        conditional: instituteInvoiceAddressConditionalShow,
      },
      {
        label: <FormattedMessage id="registration.invoiceCountry" />,
        key: 'institute.invoice.invoiceCountry',
        type: 'select',
        columnClassName: 'basis-50',
        options: buildCountryOptions(locale, isAdmin),
        validate: requiredField,
        conditional: instituteInvoiceAddressConditionalShow,
      },
      {
        label: <FormattedMessage id="registration.certificatedifferent" />,
        key: 'institute.certificatedifferent',
        type: 'checkbox',
        defaultValue: false,
        columnClassName: 'basis-100',
      },
      {
        label: <FormattedMessage id="registration.instituteNameWithLeadName" />,
        type: 'info',
        key: 'institute.certificate.info',
        conditional: instituteCertificateAddressConditionalShow,
      },
      {
        label: <FormattedMessage id="registration.hospitalName" />,
        key: 'institute.certificate.certificationHospitalName',
        columnClassName: 'basis-100',
        type: 'text',
        conditional: instituteCertificateAddressConditionalShow,
      },
      {
        label: <FormattedMessage id="registration.instituteName" />,
        key: 'institute.certificate.certificationName',
        columnClassName: 'basis-50',
        type: 'text',
        validate: requiredField,
        conditional: instituteCertificateAddressConditionalShow,
      },
      {
        label: <FormattedMessage id="registration.invoiceStreet" />,
        type: 'text',
        key: 'institute.certificate.certificationStreet',
        columnClassName: 'basis-50',
        validate: requiredField,
        conditional: instituteCertificateAddressConditionalShow,
      },
      {
        label: <FormattedMessage id="registration.invoiceHousenumber" />,
        type: 'text',
        key: 'institute.certificate.certificationHousenumber',
        columnClassName: 'basis-50',
        validate: requiredField,
        conditional: instituteCertificateAddressConditionalShow,
      },
      {
        label: <FormattedMessage id="registration.invoiceZip" />,
        type: 'text',
        key: 'institute.certificate.certificationZip',
        columnClassName: 'basis-50',
        validate: requiredField,
        conditional: instituteCertificateAddressConditionalShow,
      },
      {
        label: <FormattedMessage id="registration.invoiceCity" />,
        type: 'text',
        key: 'institute.certificate.certificationCity',
        columnClassName: 'basis-50',
        validate: requiredField,
        conditional: instituteCertificateAddressConditionalShow,
      },
      {
        label: <FormattedMessage id="registration.invoiceCountry" />,
        key: 'institute.certificate.certificationCountry',
        type: 'select',
        columnClassName: 'basis-50',
        options: buildCountryOptions(locale, isAdmin),
        validate: requiredField,
        conditional: instituteCertificateAddressConditionalShow,
      },
      {
        label: <FormattedMessage id="account.forwardAddressesHeading" />,
        type: 'info',
        key: 'institute.forward.info',
        hide: true,
      },
      {
        label: 'E-Mail Address 1',
        key: 'institute.forward.email1',
        type: 'text',
        columnClassName: 'basis-100',
        hide: true,
      },
      {
        label: 'E-Mail Address 2',
        key: 'institute.forward.email2',
        type: 'text',
        columnClassName: 'basis-100',
        hide: true,
      },
      {
        label: 'E-Mail Address 3',
        key: 'institute.forward.email3',
        type: 'text',
        columnClassName: 'basis-100',
        hide: true,
      },
    ],
    requiredMessage: <FormattedMessage id="registration.requiredMessage" />,
    note: <FormattedMessage id="registration.patientCareDescription" />,
  },
];

export default registerFormConfig;
